/**
 * Pós-processamento de respostas Orya (texto, export, hierarquia).
 */

import {
  assertOryaTableResult,
  countEquipamentoBlocksInMarkdown,
  syncPanelMarkdownWithEquipBlocks,
} from './relatoriosOryaTableAssert';
import {
  buildHierarchicalPanelIntro,
  buildHierarchicalPanelMarkdown,
  buildPartsFocusedFromReportRows,
  countMarkdownTables,
  resolveEquipRowsForPartsFocus,
  shouldEnrichPanelWithChildBlocks,
  shouldUsePartsFocusedLayout,
  stripPrematureChildDetailFromIntro,
} from './relatoriosOryaHierarchyDisplay';

/** Converte datas ISO (yyyy-MM-dd) para dd/MM/yyyy no texto exibido. */
export function formatOryaDatesBr(text) {
  return String(text || '').replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, y, mo, d) => {
    return `${d}/${mo}/${y}`;
  });
}

/** Remove só quantidade da amostra e período de OS; mantém o restante da introdução. */
export function stripOryaBoilerplateIntro(text) {
  let t = String(text || '');
  t = t.replace(/Com base nos dados da amostra de\s+[\d.]+\s+equipamentos\s*/gi, '');
  t = t.replace(/Com base nos dados da amostra[^.,;\n]*?\s*/gi, '');
  t = t.replace(/,?\s*considerando o período de OS[^.,;\n]*/gi, '');
  t = t.replace(/,?\s*período de OS de\s+[^.,;\n]*/gi, '');
  t = t.replace(/^\s*[,;]\s*/, '');
  t = t.trim();
  if (t && /^[a-záàâãéêíóôõúç]/.test(t)) {
    t = t.charAt(0).toUpperCase() + t.slice(1);
  }
  return formatOryaDatesBr(t.replace(/\n{3,}/g, '\n\n').trim());
}

function ensurePanelIntro(intro) {
  const t = stripOryaBoilerplateIntro(intro);
  if (t) return t;
  return 'Segue o que encontrei na análise:';
}

/** Emoji só em títulos ou avisos pontuais — não em cada linha. */
const TITLE_EMOJI = [
  { re: /^#{1,4}\s+.*peças/i, emoji: '🔧' },
  { re: /^#{1,4}\s+.*equipamento/i, emoji: '🖨️' },
  { re: /atenção|observa|nota:/i, emoji: '💡' },
];

export function enrichOryaProseLine(line) {
  const trimmed = String(line || '').trim();
  if (!trimmed) return { emoji: null, text: '' };
  const isHeading = /^#{1,4}\s/.test(trimmed) || /^\*\*[^*]+\*\*\s*$/.test(trimmed);
  if (isHeading) {
    for (const { re, emoji } of TITLE_EMOJI) {
      if (re.test(trimmed)) return { emoji, text: trimmed };
    }
    return { emoji: null, text: trimmed };
  }
  return { emoji: null, text: trimmed };
}

function extractTableMarkdown(md) {
  const lines = String(md || '').split(/\r?\n/);
  const start = lines.findIndex((l) => l.trim().startsWith('|'));
  if (start < 0) return '';
  const chunk = [];
  for (let i = start; i < lines.length; i += 1) {
    if (lines[i].trim().startsWith('|')) chunk.push(lines[i]);
    else if (chunk.length) break;
  }
  return chunk.join('\n').trim();
}

function tableToMarkdown(headers, rows) {
  if (!headers?.length) return '';
  const sep = headers.map(() => '---');
  const body = (rows || []).map((r) =>
    headers
      .map((_, i) => formatOryaDatesBr(String(r[i] ?? '').replace(/\|/g, '\\|')))
      .join(' | ')
  );
  return [
    `| ${headers.join(' | ')} |`,
    `| ${sep.join(' | ')} |`,
    ...body.map((line) => `| ${line} |`),
  ].join('\n');
}

export function postProcessOryaPanelResult(parsed, meta = {}) {
  if (!parsed?.useMainPanel) {
    return {
      ...parsed,
      chatContent: formatOryaDatesBr(parsed.chatContent),
    };
  }

  const lines = String(parsed.panelMarkdown || '').split(/\r?\n/);
  const tableStart = lines.findIndex((l) => l.trim().startsWith('|'));
  const proseRaw =
    parsed.intro ||
    (tableStart > 0 ? lines.slice(0, tableStart).join('\n') : '');
  let intro = ensurePanelIntro(stripPrematureChildDetailFromIntro(proseRaw));

  let headers = parsed.headers || [];
  let rows = parsed.rows || [];

  const asserted = assertOryaTableResult({
    headers,
    rows,
    intro,
    rawContent: meta.rawContent ?? parsed.panelMarkdown,
    finishReason: meta.finishReason ?? null,
  });
  headers = asserted.headers;
  rows = asserted.rows;
  intro = asserted.intro;

  let tableMd =
    headers.length && rows.length
      ? tableToMarkdown(headers, rows)
      : extractTableMarkdown(parsed.panelMarkdown);

  const useHierarchy =
    headers.length &&
    rows.length &&
    meta.pecasLookup?.size &&
    meta.effectiveColumnRefs &&
    shouldEnrichPanelWithChildBlocks({
      lastUserMessage: meta.lastUserMessage,
      intro,
    });

  if (useHierarchy) {
    const partsFocused = shouldUsePartsFocusedLayout(meta.lastUserMessage);
    let hierarchical = null;
    let equipCount = rows.length;

    if (partsFocused && meta.sourceRows?.length) {
      const equipRows = resolveEquipRowsForPartsFocus(
        headers,
        rows,
        meta.sourceRows,
        meta.pecasLookup,
        meta.rawContent
      );
      equipCount = equipRows.length || rows.length;
      hierarchical = buildPartsFocusedFromReportRows(
        equipRows,
        meta.pecasLookup,
        meta.effectiveColumnRefs,
        { lastUserMessage: meta.lastUserMessage }
      );
    }

    if (!hierarchical) {
      hierarchical = buildHierarchicalPanelMarkdown(
        headers,
        rows,
        meta.pecasLookup,
        meta.effectiveColumnRefs,
        { lastUserMessage: meta.lastUserMessage }
      );
    }

    if (hierarchical) {
      tableMd = hierarchical;
      const blockCount = countEquipamentoBlocksInMarkdown(tableMd);
      if (blockCount > 0) equipCount = blockCount;
      intro = partsFocused
        ? buildHierarchicalPanelIntro('', equipCount)
        : buildHierarchicalPanelIntro(intro, equipCount);
    }
  }

  let panelMarkdown = formatOryaDatesBr([intro, tableMd].filter(Boolean).join('\n\n'));
  panelMarkdown = syncPanelMarkdownWithEquipBlocks(panelMarkdown);

  const tableCount = countMarkdownTables(panelMarkdown);

  return {
    ...parsed,
    intro: intro || null,
    panelMarkdown,
    headers,
    rows,
    tableTruncated: asserted.truncated,
    tableCount,
    canExportXlsx: tableCount <= 1,
  };
}
